import * as React from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import { throttle } from 'throttle-debounce';

import { ELocalStorageMobile } from '@enums';

import { CurrentUserStore, ICountersStore, IUiStoreParams } from '@stores';

import { getConfig, URLS } from '@base/constants';
import { EStore } from '@base/enums';
import { useCountersWatchers } from '@base/hooks';
import { isMobileScreen, getTranslation, setToLocalStorage, getFromLocalStorage } from '@base/utils';

import { Alert } from '@components';

import { CatalogTabs } from '@ui';

import { Header } from './Header';
import { useSticky } from './hooks';

import * as s from './TopMenu.scss';

export interface ITopMenuProps {
	uiStore?: IUiStoreParams;
	countersStore?: ICountersStore;
	currentUserStore?: CurrentUserStore;
}

export const TopMenu: React.FC<ITopMenuProps> = inject(
	EStore.UI,
	EStore.COUNTERS,
	EStore.CURRENT_USER,
)(observer((
	{
		uiStore,
		countersStore,
		currentUserStore,
	}: ITopMenuProps,
) => {
	const t = getTranslation('components.TopMenu');

	const { topMenuBurgerButton, slidingMenu } = uiStore;
	const {
		notifies,
		dialogues,
	} = countersStore;

	const history = useHistory();
	const { pathname } = useLocation();

	const [backPath, setBackPath] = React.useState('');
	const [showCatalogTabs, setShowCatalogTabs] = React.useState(true);
	const headerRef = React.useRef<HTMLDivElement>(null);
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const stickyHeaderRef = React.useRef<HTMLDivElement>(null);
	const [isRecommendTechAlertHidden, setIsRecommendTechAlertHidden] = React.useState(
		getFromLocalStorage(ELocalStorageMobile.SHOWN_RECOMMEND_TECH_INFO) === '1',
	);
	const recommendTechAlertUrl = getConfig().hosts.recommendTechInfoUrl;
	const shouldShowRecommendTechAlert = Boolean(recommendTechAlertUrl) &&
		!IS_SSR &&
		!isRecommendTechAlertHidden;

	const shift = headerRef?.current?.clientHeight;
	const theme = getConfig().appearance.theme;

	const {
		isFixed,
		isAnimated,
		isVisible,
		isTransparent,
	} = useSticky({
		wrapperRef,
		stickyHeaderRef,
		heightToStick: shift || 48,
	});
	const transformShift = `${shift}px` || '100%';
	const transformArray = [
		'WebkitTransform',
		'MozTransform',
		'MsTransform',
		'OTransform',
		'transform',
	];
	const stickyHeaderAddStyle = !isVisible
		? transformArray.reduce((obj, item) => {
			return {
				...obj,
				[`${item}`]: `translate(0, -${transformShift})`,
			};
		}, {})

		: { transform: `translate(0, 0)` };

	const onBurgerBtnClick = () => {
		if (topMenuBurgerButton.isBackMode) {
			if (uiStore.topMenuBurgerButton.backUrl) {
				history.push(uiStore.topMenuBurgerButton.backUrl);
			} else if (backPath) {
				const id = backPath?.split('apps/')[1];
				const isPrevSeoAppPage = id ? /^\d+$/g.test(id) : false;

				if (isPrevSeoAppPage) {
					history.push(URLS.APPS);
				} else {
					history.push(backPath);
				}
			} else {
				history.push(URLS.APPS);
			}
			return;
		}

		uiStore.toggleSlideMenu(!slidingMenu.isVisible);
	};

	const onRecommendTechAlertCloseClick = () => {
		setIsRecommendTechAlertHidden(true);
		setToLocalStorage(ELocalStorageMobile.SHOWN_RECOMMEND_TECH_INFO, '1');
	};

	useCountersWatchers({
		countersStore,
		currentUserStore,
	});

	React.useEffect(() => {
		return () => {
			setBackPath(pathname);
		};
	}, [pathname]);

	React.useEffect(() => {
		setShowCatalogTabs(isMobileScreen());
	}, []);

	React.useEffect(() => {
		const onResize = throttle(300, () => {
			setShowCatalogTabs(isMobileScreen());
		});

		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, []);

	return (
		<div className={s.wrapper} ref={wrapperRef}>
			<div
				className={
					classNames(
						s.stickyHeader,
						{ [`use-theme-${theme}`]: Boolean(theme) },
						{ [s.fixed]: isFixed },
						{ [s.animated]: isAnimated },
					)
				}
				ref={stickyHeaderRef}
				style={stickyHeaderAddStyle}
			>
				<Header
					refProp={headerRef}
					isTransparent={isTransparent}
					counters={{
						notifies,
						dialogues,
					}}
					isShowIcons={(!getConfig().isTwa && currentUserStore.isMyCreated) && uiStore.topMenuIcons.isVisible}
					isBurgerInBackState={topMenuBurgerButton.isBackMode}
					onBurgerBtnClick={onBurgerBtnClick}
					pageTitle={uiStore.pageTitle.isVisible ? uiStore.pageTitle?.value : null}

				/>
				{showCatalogTabs && <CatalogTabs className={s.tabs}/>}
				{shouldShowRecommendTechAlert && <Alert
					className={s.alert}
					text={t('alertRecommendText')}
					more={{
						title: t('alertRecommendMoreTitle'),
						url: getConfig().hosts.recommendTechInfoUrl,
					}}
					onClose={onRecommendTechAlertCloseClick}
				/>}
			</div>
		</div>
	);
}));
